import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { articles } from '@/mocks/articles';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const guidesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b-2 border-background-200/70'
            : 'bg-background-50 border-b border-background-200/40'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" onClick={closeMobile}>
            <span className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950 tracking-tight">
              SUGARGOO
            </span>
            <span className="hidden sm:inline text-[10px] font-bold text-foreground-500 bg-background-200/60 px-2 py-0.5 rounded-full tracking-wide">
              SPREADSHEET
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Home
            </Link>
            <Link to="/sugargoo-spreadsheet-guide" className="text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap">
              Beginner Guide
            </Link>
            <div ref={guidesRef} className="relative">
              <button
                onClick={() => setGuidesOpen(!guidesOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                Shopping Guides
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${guidesOpen ? 'rotate-180' : ''}`}></i>
                </span>
              </button>
              {guidesOpen && (
                <div className="absolute top-full mt-2 left-0 bg-background-50 border-2 border-background-200/80 rounded-xl py-2 min-w-[240px] z-50">
                  {articles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/articles/${article.slug}`}
                      onClick={() => setGuidesOpen(false)}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    >
                      {article.title.length > 45 ? article.title.slice(0, 45) + '...' : article.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-bold bg-foreground-950 text-background-50 px-5 py-2.5 rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-200/60 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </nav>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/40" onClick={closeMobile}></div>
          <div className="absolute top-16 left-0 right-0 bg-background-50 border-b-2 border-background-200/60 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="p-4 space-y-1">
              <Link to="/" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors">
                <span className="w-5 h-5 flex items-center justify-center text-foreground-600"><i className="ri-home-line"></i></span>
                <span className="text-sm font-semibold text-foreground-950">Home</span>
              </Link>
              <Link to="/sugargoo-spreadsheet-guide" onClick={closeMobile} className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors">
                <span className="w-5 h-5 flex items-center justify-center text-foreground-600"><i className="ri-book-open-line"></i></span>
                <span className="text-sm font-semibold text-foreground-950">Beginner Guide</span>
              </Link>
              <div className="border-t border-background-200/60 my-2"></div>
              <p className="px-3 py-2 text-xs font-bold text-foreground-400 uppercase tracking-wider">Shopping Guides</p>
              {articles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/articles/${article.slug}`}
                  onClick={closeMobile}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-500"><i className="ri-article-line"></i></span>
                  <span className="text-sm text-foreground-700">{article.title.length > 50 ? article.title.slice(0, 50) + '...' : article.title}</span>
                </Link>
              ))}
              <div className="border-t border-background-200/60 my-2"></div>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center gap-3 px-3 py-3 rounded-lg bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-colors mt-2"
              >
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-shopping-bag-line"></i></span>
                <span className="text-sm font-bold">Buy It Now</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}